import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { AlertTriangleIcon } from '../icons';
import Logo from '../Logo';

export default function AdminLoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);
  const { loginAdmin, auth }    = useAuth();
  const navigate                = useNavigate();

  // Already logged in → go straight to dashboard
  useEffect(() => {
    if (auth?.role === 'admin') navigate('/admin', { replace: true });
  }, [auth, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password.trim()) return;
    setError(''); setLoading(true);
    try {
      await loginAdmin(username.trim(), password.trim());
      navigate('/admin', { replace: true });
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid username or password.');
    } finally { setLoading(false); }
  };

  return (
    <div style={s.page}>
      <div style={s.left}>
        <div style={s.brand}>
          <div style={s.brandIcon}><Logo size={34} /></div>
          <div style={s.brandName}>Picklers Lounge</div>
        </div>
        <div style={s.leftContent}>
          <h1 style={s.headline}>Court Recording,<br />Simplified.</h1>
          <p style={s.tagline}>Manage all 4 courts, sessions, and recordings from one place.</p>
          <div style={s.features}>
            {['Live court status monitoring','Automatic email delivery','7-day video retention','4 simultaneous recordings'].map(f => (
              <div key={f} style={s.feature}>
                <span style={s.featureDot} />
                {f}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={s.right}>
        <div style={s.card}>
          <div style={s.cardHeader}>
            <h2 style={s.cardTitle}>Admin Sign In</h2>
            <p style={s.cardSub}>Enter your credentials to access the dashboard</p>
          </div>

          <form onSubmit={handleSubmit} style={s.form}>
            <div style={s.field}>
              <label style={s.label}>Username</label>
              <input
                type="text"
                value={username}
                onChange={e => { setUsername(e.target.value); setError(''); }}
                placeholder="admin"
                style={s.input}
                autoComplete="username"
                autoFocus
              />
            </div>
            <div style={s.field}>
              <label style={s.label}>Password</label>
              <input
                type="password"
                value={password}
                onChange={e => { setPassword(e.target.value); setError(''); }}
                placeholder="••••••••"
                style={s.input}
                autoComplete="current-password"
              />
            </div>

            {error && (
              <div style={s.errorBox}>
                <span style={s.errorIcon}><AlertTriangleIcon size={14} /></span> {error}
              </div>
            )}

            <button
              type="submit"
              disabled={!username.trim() || !password.trim() || loading}
              style={{
                ...s.btn,
                opacity: (!username.trim() || !password.trim() || loading) ? 0.55 : 1,
              }}
            >
              {loading ? (
                <span style={s.spinner} />
              ) : 'Sign In'}
            </button>
          </form>

          <p style={s.kioskLink}>
            Looking for the player kiosk?{' '}
            <a href="/" style={s.link}>Open Kiosk →</a>
          </p>
        </div>
      </div>
    </div>
  );
}

const s = {
  page: {
    display: 'flex', minHeight: '100vh',
    fontFamily: 'var(--font)',
  },
  left: {
    flex: 1, background: 'var(--blue)',
    padding: '48px 56px',
    display: 'flex', flexDirection: 'column',
    justifyContent: 'space-between',
    '@media(max-width:768px)': { display: 'none' },
  },
  brand: {
    display: 'flex', alignItems: 'center', gap: 10,
  },
  brandIcon: { fontSize: 28 },
  brandName: {
    fontSize: 20, fontWeight: 700, color: 'white',
    letterSpacing: '-0.3px',
  },
  leftContent: { flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' },
  headline: {
    fontSize: 44, fontWeight: 700, color: 'white',
    lineHeight: 1.15, letterSpacing: '-1px',
    marginBottom: 16,
  },
  tagline: {
    fontSize: 17, color: 'rgba(255,255,255,0.75)',
    lineHeight: 1.6, marginBottom: 40,
  },
  features: { display: 'flex', flexDirection: 'column', gap: 14 },
  feature: {
    display: 'flex', alignItems: 'center', gap: 12,
    color: 'rgba(255,255,255,0.9)', fontSize: 15,
  },
  featureDot: {
    width: 7, height: 7, borderRadius: '50%',
    background: 'rgba(255,255,255,0.6)',
    flexShrink: 0,
  },
  right: {
    width: 480, display: 'flex',
    alignItems: 'center', justifyContent: 'center',
    padding: 40, background: 'var(--white)',
  },
  card: { width: '100%', maxWidth: 380 },
  cardHeader: { marginBottom: 32 },
  cardTitle: {
    fontSize: 26, fontWeight: 700, color: 'var(--gray-900)',
    letterSpacing: '-0.5px', marginBottom: 6,
  },
  cardSub: { fontSize: 14, color: 'var(--gray-500)' },
  form: { display: 'flex', flexDirection: 'column', gap: 20 },
  field: { display: 'flex', flexDirection: 'column', gap: 6 },
  label: {
    fontSize: 13, fontWeight: 600,
    color: 'var(--gray-700)', letterSpacing: '0.01em',
  },
  input: {
    padding: '11px 14px',
    border: '1.5px solid var(--gray-200)',
    borderRadius: 'var(--radius-sm)',
    fontSize: 15, color: 'var(--gray-900)',
    outline: 'none', transition: 'border-color 0.15s',
    background: 'var(--white)',
  },
  errorBox: {
    background: 'var(--red-light)', color: 'var(--red)',
    borderRadius: 'var(--radius-sm)', padding: '10px 14px',
    fontSize: 13, fontWeight: 500,
    display: 'flex', alignItems: 'center', gap: 8,
  },
  errorIcon: { fontSize: 15 },
  btn: {
    background: 'var(--blue)', color: 'white',
    padding: '13px', borderRadius: 'var(--radius-sm)',
    fontSize: 15, fontWeight: 600,
    border: 'none', cursor: 'pointer',
    transition: 'background 0.15s, opacity 0.15s',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    height: 48, marginTop: 4,
  },
  spinner: {
    width: 18, height: 18, borderRadius: '50%',
    border: '2px solid rgba(255,255,255,0.3)',
    borderTopColor: 'white',
    animation: 'spin 0.7s linear infinite',
    display: 'inline-block',
  },
  kioskLink: {
    marginTop: 28, textAlign: 'center',
    fontSize: 13, color: 'var(--gray-500)',
  },
  link: { color: 'var(--blue)', fontWeight: 600 },
};
